import React, { useState } from 'react';
import { DEFAULT_STROKE, DEFAULT_GRADIENT } from './svgUtils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Trash2, Copy, AlignLeft, AlignCenter, AlignRight, Bold, Italic, Underline, Layers, Settings, Eye, EyeOff, ChevronUp, ChevronDown, Image as ImageIcon, Brush, Scissors, AlignStartHorizontal, AlignCenterHorizontal, AlignEndHorizontal, AlignStartVertical, AlignCenterVertical, AlignEndVertical, AlignHorizontalDistributeCenter, AlignVerticalDistributeCenter, List, ListOrdered } from 'lucide-react';
import { mockFilters } from '../../mock/mockData';
import { EDITOR_FONTS, loadGoogleFont } from '../../lib/fonts';
import { ScrollArea } from '../ui/scroll-area';
import GradientEditor, { buildGradientCSS } from './GradientEditor';
import SvgPaletteEditor from './SvgPaletteEditor';
import ToggleSwitch from '../ui/ToggleSwitch';
import { Link2, Link2Off } from 'lucide-react';
import { isAspectLocked, resizeKeepingAspect } from './aspectLock';
import { BLEND_MODES, supportsBlendMode } from './blendModes';
import EffectGallery from './EffectGallery';
import LayersPanel from './LayersPanel';

// Renders a font name in its own face inside the Radix Select dropdown.
// IntersectionObserver triggers the Google Fonts <link> injection only when
// the row scrolls into view, so opening the menu does not fetch the entire
// catalog at once.
const FontSelectItem = ({ font }) => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) { loadGoogleFont(font); io.disconnect(); break; }
      }
    }, { rootMargin: '120px' });
    io.observe(el);
    return () => io.disconnect();
  }, [font]);
  return (
    <SelectItem ref={ref} value={font} style={{ fontFamily: `'${font}', sans-serif` }}>
      {font}
    </SelectItem>
  );
};

// Compact 4-corner radius editor. Collapsed by default — shows the existing
// All-Corners slider. Expanding reveals TL/TR/BR/BL inputs that write to
// `element.cornerRadii`. Collapsing back clears `cornerRadii` and restores the
// uniform `cornerRadius` flow so the element json stays clean for users who
// never opted into per-corner mode.
const CornerRadiusEditor = ({ element, onChange }) => {
  const uniform = Math.max(0, Number(element.cornerRadius) || 0);
  const radii = element.cornerRadii || null;
  const [expanded, setExpanded] = React.useState(!!radii);

  const setCorner = (key, raw) => {
    const v = Math.max(0, Math.min(999, Number(raw) || 0));
    const next = {
      tl: Number(radii?.tl ?? uniform) || 0,
      tr: Number(radii?.tr ?? uniform) || 0,
      br: Number(radii?.br ?? uniform) || 0,
      bl: Number(radii?.bl ?? uniform) || 0,
      [key]: v,
    };
    onChange('cornerRadii', next);
  };

  const toggleExpand = () => {
    if (expanded) {
      // Collapse — drop per-corner override.
      if (radii) onChange('cornerRadii', null);
      setExpanded(false);
    } else {
      // Expand — seed from the uniform value so visible state matches the rect.
      if (!radii) {
        onChange('cornerRadii', { tl: uniform, tr: uniform, br: uniform, bl: uniform });
      }
      setExpanded(true);
    }
  };

  const cornerInput = (key, label) => (
    <div className="flex items-center gap-1 min-w-0">
      <span className="text-[10px] text-gray-400 font-medium w-5 flex-shrink-0">{label}</span>
      <Input
        type="number"
        min={0}
        max={999}
        value={Math.max(0, Number(radii?.[key] ?? uniform) || 0)}
        onChange={(e) => setCorner(key, e.target.value)}
        className="h-7 text-xs flex-1 min-w-0"
      />
    </div>
  );

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium text-gray-700">Corner Radius</Label>
        <div className="flex items-center gap-2">
          {!expanded && (
            <span className="text-xs text-gray-500 tabular-nums">{uniform}px</span>
          )}
          <button
            type="button"
            onClick={toggleExpand}
            className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
              expanded ? 'bg-violet-100 text-violet-700' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
            }`}
            title={expanded ? 'Use uniform radius' : 'Custom corners'}
          >
            {expanded ? 'Uniform' : 'Custom'}
          </button>
        </div>
      </div>

      {!expanded && (
        <Slider
          value={[Math.max(0, Math.min(999, uniform))]}
          onValueChange={(val) => onChange('cornerRadius', Math.max(0, Math.min(999, Number(val[0]) || 0)))}
          min={0} max={999} step={1}
        />
      )}

      {expanded && (
        <div className="grid grid-cols-2 gap-1.5">
          {cornerInput('tl', 'TL')}
          {cornerInput('tr', 'TR')}
          {cornerInput('bl', 'BL')}
          {cornerInput('br', 'BR')}
        </div>
      )}
    </div>
  );
};

const BRUSH_PRESET_OPTIONS = [
  { id: 'basic',       name: 'Basic',       description: 'Smooth round stroke' },
  { id: 'marker',      name: 'Marker',      description: 'Flat semi-transparent' },
  { id: 'calligraphy', name: 'Calligraphy', description: 'Angled nib pen' },
  { id: 'chalk',       name: 'Chalk',       description: 'Rough powdery edges' },
  { id: 'spray',       name: 'Spray',       description: 'Scattered spray paint' },
  { id: 'texture',     name: 'Texture',     description: 'Bristle texture brush' },
];

const RightSidebar = ({ selectedElement, selectedElements = [], onUpdateElement, onUpdateElementWithHistory, onDeleteElement, onDeleteSelectedElements, onDuplicateElement, onDuplicateSelectedElements, elements, onSelectElement, onMoveLayer, onReorderLayer, onApplyMask, onRemoveMask, onReplaceImage, brandKit, activeTool, brushSettings, setBrushSettings, selectedBrushPreset, setSelectedBrushPreset, eraserSettings, setEraserSettings, onPreviewFilter, onAlignSelected, onDistributeSelected }) => {
  const [activeTab, setActiveTab] = useState('properties');
  const replaceImageInputRef = React.useRef(null);
  // support when selection is stored in selectedElements instead of selectedElement
  const effectiveSelected = selectedElement ?? (selectedElements && selectedElements.length > 0 ? selectedElements[0] : null);
  // If selectedElement prop is not set but effectiveSelected exists (from selectedElements),
  // assign it so existing references to `selectedElement` work without changing every usage.
  if (!selectedElement && effectiveSelected) {
    // eslint-disable-next-line no-param-reassign
    selectedElement = effectiveSelected;
  }
  const handlePropertyChange = (property, value) => {
    if (!effectiveSelected) return;
    onUpdateElement(effectiveSelected.id, { [property]: value });
  };

  // Discrete property commit that creates a single undo entry (blend mode,
  // effect/shadow presets). Falls back to the plain update if no history
  // handler is wired.
  const handleCommitProperty = (property, value) => {
    if (!effectiveSelected) return;
    (onUpdateElementWithHistory || onUpdateElement)(effectiveSelected.id, { [property]: value });
  };

  // Width/height edits honor the aspect lock: when locked, the other dimension
  // scales proportionally. Single update so it's one undo step.
  const handleDimensionChange = (dim, value) => {
    if (!effectiveSelected) return;
    const locked = isAspectLocked(effectiveSelected);
    onUpdateElement(effectiveSelected.id, resizeKeepingAspect(effectiveSelected, dim, value, locked));
  };

  const toggleAspectLock = () => {
    if (!effectiveSelected) return;
    onUpdateElement(effectiveSelected.id, { aspectLocked: !isAspectLocked(effectiveSelected) });
  };

  return (
    <div className="w-60 lg:w-64 xl:w-72 border-l bg-white flex flex-col h-full relative overflow-hidden">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        <div className="border-b px-3 py-3 flex-shrink-0">
          <TabsList className="w-full flex items-center gap-2">
            <TabsTrigger value="properties" className="flex-1 flex items-center justify-center gap-1 text-xs">
              <Settings className="w-3.5 h-3.5" />
              Properties
            </TabsTrigger>
            <TabsTrigger value="layers" className="flex-1 flex items-center justify-center gap-1 text-xs">
              <Layers className="w-3.5 h-3.5" />
              Layers
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Properties Tab */}
        {activeTab === 'properties' && (
          <TabsContent value="properties" className="flex-1 m-0 flex flex-col min-h-0">
            <ScrollArea className="flex-1 h-0 overflow-x-hidden">
              {/* Eraser settings — always shown when eraser tool is active */}
              {activeTool === 'eraser' && eraserSettings && setEraserSettings ? (
                <div className="py-4 px-3 space-y-5" style={{ boxSizing: 'border-box' }}>
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Scissors className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-semibold text-gray-800">Eraser Settings</span>
                  </div>
                  {/* Size */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Size</Label>
                      <span className="text-xs tabular-nums text-gray-700 font-medium">{eraserSettings.size}px</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Slider
                        value={[eraserSettings.size]}
                        onValueChange={([v]) => setEraserSettings(s => ({ ...s, size: v }))}
                        min={1} max={300} step={1}
                        className="flex-1"
                      />
                      <Input
                        type="number" min={1} max={300}
                        value={eraserSettings.size}
                        onChange={e => setEraserSettings(s => ({ ...s, size: Math.max(1, Math.min(300, Number(e.target.value) || 1)) }))}
                        className="w-14 text-center text-xs"
                      />
                    </div>
                  </div>
                  {/* Hardness */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hardness</Label>
                      <span className="text-xs tabular-nums text-gray-700 font-medium">{eraserSettings.hardness}%</span>
                    </div>
                    <Slider
                      value={[eraserSettings.hardness]}
                      onValueChange={([v]) => setEraserSettings(s => ({ ...s, hardness: v }))}
                      min={0} max={100} step={5}
                      className="w-full"
                    />
                    <p className="text-[10px] text-gray-400 leading-snug">
                      {eraserSettings.hardness <= 30
                        ? 'Very soft — smooth gradient fade at edges'
                        : eraserSettings.hardness <= 70
                        ? 'Medium — slight softening at stroke edges'
                        : 'Hard — sharp, clean erase edges'}
                    </p>
                  </div>
                  {/* Opacity */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Opacity</Label>
                      <span className="text-xs tabular-nums text-gray-700 font-medium">{Math.round(eraserSettings.opacity * 100)}%</span>
                    </div>
                    <Slider
                      value={[Math.round(eraserSettings.opacity * 100)]}
                      onValueChange={([v]) => setEraserSettings(s => ({ ...s, opacity: v / 100 }))}
                      min={5} max={100} step={5}
                      className="w-full"
                    />
                    <p className="text-[10px] text-gray-400">Lower opacity = partial erase (semi-transparent)</p>
                  </div>
                </div>

              /* Clipping Mask panel — always shown when mask tool is active */
              ) : activeTool === 'mask' ? (
                <div className="py-4 px-3 space-y-5" style={{ boxSizing: 'border-box' }}>
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <svg className="w-4 h-4 text-indigo-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="9" cy="9" r="7" strokeDasharray="3.5 2" />
                      <circle cx="15" cy="15" r="7" />
                      <path d="M16 8 A7 7 0 0 1 8 16 A7 7 0 0 1 16 8Z" fill="currentColor" fillOpacity="0.12" stroke="none" />
                    </svg>
                    <span className="text-sm font-semibold text-gray-800">Clipping Mask</span>
                  </div>

                  {/* Step-by-step instructions */}
                  <div className="space-y-2.5">
                    {[
                      { n: 1, text: <>Click to select the <strong>mask shape</strong> — the shape that defines the visible area (circle, star, text, etc.)</> },
                      { n: 2, text: <>Click again to add the <strong>content</strong> — the image or element to clip inside the shape</> },
                      { n: 3, text: <>Click <strong>Apply Mask</strong> below</> },
                    ].map(({ n, text }) => (
                      <div key={n} className="flex items-start gap-2.5">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-[11px] font-bold flex items-center justify-center mt-0.5">{n}</span>
                        <p className="text-xs text-gray-600 leading-snug">{text}</p>
                      </div>
                    ))}
                  </div>

                  {/* Selection summary */}
                  {selectedElements && selectedElements.length > 0 ? (() => {
                    const sorted = [...selectedElements].sort((a, b) => {
                      const ia = (elements || []).findIndex(el => el.id === a.id);
                      const ib = (elements || []).findIndex(el => el.id === b.id);
                      return ib - ia;
                    });
                    const maskEl = sorted[0];
                    const contentEls = sorted.slice(1);
                    return (
                      <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-2">
                        <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{selectedElements.length} selected</p>
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />
                            <span className="text-xs text-gray-700 truncate">
                              <span className="font-semibold text-indigo-700">Mask: </span>
                              {maskEl.type}{maskEl.text ? ` "${maskEl.text.substring(0, 14)}"` : ''}
                            </span>
                          </div>
                          {contentEls.map(el => (
                            <div key={el.id} className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-slate-400 flex-shrink-0" />
                              <span className="text-xs text-gray-700 truncate">
                                <span className="font-semibold text-slate-600">Content: </span>
                                {el.type}{el.text ? ` "${el.text.substring(0, 14)}"` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                        {selectedElements.length < 2 && (
                          <p className="text-[10px] text-amber-600 pt-1">Select at least 2 elements to apply.</p>
                        )}
                      </div>
                    );
                  })() : (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                      <p className="text-xs text-amber-700 leading-snug">No elements selected. Click objects on the canvas to build your mask group.</p>
                    </div>
                  )}

                  {/* Apply Mask button */}
                  <Button
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                    disabled={!selectedElements || selectedElements.length < 2 || !onApplyMask}
                    onClick={() => {
                      if (!selectedElements || selectedElements.length < 2 || !onApplyMask) return;
                      const sorted = [...selectedElements].sort((a, b) => {
                        const ia = (elements || []).findIndex(el => el.id === a.id);
                        const ib = (elements || []).findIndex(el => el.id === b.id);
                        return ib - ia;
                      });
                      onApplyMask(sorted[0].id, sorted.slice(1).map(el => el.id));
                    }}
                  >
                    Apply Mask
                  </Button>

                  {/* Remove Mask / Replace Image — shown when a masked element is selected */}
                  {selectedElement && (selectedElement.isMaskShape || selectedElement.clippedTo || selectedElement.clipMask) && (
                    <div className="border-t pt-4 space-y-2">
                      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Active Mask</p>

                      {/* Replace Image — only for the image content inside a mask */}
                      {selectedElement.type === 'image' && selectedElement.clippedTo && (
                        <>
                          <Button
                            variant="outline"
                            className="w-full text-indigo-600 border-indigo-200 hover:bg-indigo-50 gap-2"
                            onClick={() => replaceImageInputRef.current?.click()}
                          >
                            <ImageIcon className="w-3.5 h-3.5" /> Replace Image
                          </Button>
                          <input
                            ref={replaceImageInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file || !onUpdateElement) return;
                              const reader = new FileReader();
                              reader.onload = (ev) => {
                                onUpdateElement(selectedElement.id, { src: ev.target.result });
                              };
                              reader.readAsDataURL(file);
                              e.target.value = '';
                            }}
                          />
                          <p className="text-[10px] text-gray-400 leading-snug">
                            Upload a new image — the mask shape will stay unchanged.
                          </p>
                        </>
                      )}

                      <Button
                        variant="outline"
                        className="w-full text-rose-600 border-rose-200 hover:bg-rose-50"
                        onClick={() => onRemoveMask && onRemoveMask(selectedElement.id)}
                      >
                        Remove Mask
                      </Button>
                    </div>
                  )}
                </div>

              /* Brush settings — shown when brush active and no element selected */
              ) : !selectedElement && (!selectedElements || selectedElements.length === 0) && activeTool === 'brush' && brushSettings && setBrushSettings ? (
                <div className="py-4 px-3 space-y-5" style={{ boxSizing: 'border-box' }}>
                  <div className="flex items-center gap-2 pb-2 border-b">
                    <Brush className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-semibold text-gray-800">Brush Settings</span>
                  </div>
                  {/* Brush Type */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Brush Type</Label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {BRUSH_PRESET_OPTIONS.map(preset => (
                        <button
                          key={preset.id}
                          onClick={() => setSelectedBrushPreset && setSelectedBrushPreset(preset.id)}
                          title={preset.description}
                          className={`px-2 py-1.5 rounded-md text-xs font-medium text-left transition-all border ${
                            selectedBrushPreset === preset.id
                              ? 'bg-purple-100 border-purple-400 text-purple-800'
                              : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-purple-300 hover:bg-purple-50'
                          }`}
                        >
                          {preset.name}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Color */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Color</Label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={brushSettings.color}
                        onChange={e => setBrushSettings(s => ({ ...s, color: e.target.value }))}
                        className="w-9 h-9 rounded-md cursor-pointer border border-gray-300 p-0.5"
                      />
                      <Input
                        type="text"
                        value={brushSettings.color}
                        onChange={e => setBrushSettings(s => ({ ...s, color: e.target.value }))}
                        className="flex-1 font-mono text-xs"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                  {/* Size */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Size</Label>
                      <span className="text-xs tabular-nums text-gray-700 font-medium">{brushSettings.size}px</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Slider
                        value={[brushSettings.size]}
                        onValueChange={([v]) => setBrushSettings(s => ({ ...s, size: v }))}
                        min={1} max={500} step={1}
                        className="flex-1"
                      />
                      <Input
                        type="number" min={1} max={500}
                        value={brushSettings.size}
                        onChange={e => setBrushSettings(s => ({ ...s, size: Math.max(1, Math.min(500, Number(e.target.value) || 1)) }))}
                        className="w-14 text-center text-xs"
                      />
                    </div>
                  </div>
                  {/* Opacity */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Opacity</Label>
                      <span className="text-xs tabular-nums text-gray-700 font-medium">{Math.round(brushSettings.opacity * 100)}%</span>
                    </div>
                    <Slider
                      value={[Math.round(brushSettings.opacity * 100)]}
                      onValueChange={([v]) => setBrushSettings(s => ({ ...s, opacity: v / 100 }))}
                      min={5} max={100} step={5}
                      className="w-full"
                    />
                  </div>
                </div>

              /* Empty state — no tool panel, no element selected */
              ) : !selectedElement && (!selectedElements || selectedElements.length === 0) ? (
                <div className="p-6 flex items-center justify-center text-gray-400 h-full">
                  <p className="text-sm text-center">Select an element to edit its properties. Shift+click to multi-select.</p>
                </div>

              /* Element properties */
              ) : (
                <div className="py-3 px-3 pr-4 space-y-1" style={{ boxSizing: 'border-box' }}>
                  {/* Multi-layer selection banner + alignment toolbar.
                      Align operates on the selection's own bbox (Figma/Canva
                      style). Distribute spaces interior elements evenly along
                      the chosen axis and needs 3+ selected to do anything. */}
                  {selectedElements && selectedElements.length > 1 && (
                    <div className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 mb-3 space-y-2">
                      <div>
                        <p className="text-sm font-medium text-purple-800">{selectedElements.length} layers selected</p>
                        <p className="text-xs text-purple-600 mt-0.5">Drag to move all • Delete to remove • Arrow keys to nudge</p>
                      </div>
                      <div>
                        <Label className="text-[10px] text-purple-700 uppercase tracking-wide mb-1 block">Align</Label>
                        <div className="grid grid-cols-8 gap-1">
                          {[
                            { mode: 'left',    Icon: AlignStartVertical,   title: 'Align left' },
                            { mode: 'centerH', Icon: AlignCenterVertical,  title: 'Align center (horizontal)' },
                            { mode: 'right',   Icon: AlignEndVertical,     title: 'Align right' },
                            { mode: 'top',     Icon: AlignStartHorizontal, title: 'Align top' },
                            { mode: 'centerV', Icon: AlignCenterHorizontal, title: 'Align middle (vertical)' },
                            { mode: 'bottom',  Icon: AlignEndHorizontal,   title: 'Align bottom' },
                          ].map(({ mode, Icon, title }) => (
                            <button
                              key={mode}
                              type="button"
                              title={title}
                              aria-label={title}
                              onClick={() => onAlignSelected && onAlignSelected(mode)}
                              className="inline-flex items-center justify-center h-7 rounded-md border border-purple-200 bg-white text-purple-700 hover:bg-purple-100"
                            >
                              <Icon className="w-3.5 h-3.5" />
                            </button>
                          ))}
                          {[
                            { axis: 'horizontal', Icon: AlignHorizontalDistributeCenter, title: 'Distribute horizontally (3+)' },
                            { axis: 'vertical',   Icon: AlignVerticalDistributeCenter,   title: 'Distribute vertically (3+)' },
                          ].map(({ axis, Icon, title }) => {
                            const enabled = selectedElements.length >= 3;
                            return (
                              <button
                                key={axis}
                                type="button"
                                title={title}
                                aria-label={title}
                                disabled={!enabled}
                                onClick={() => onDistributeSelected && onDistributeSelected(axis)}
                                className={`inline-flex items-center justify-center h-7 rounded-md border border-purple-200 ${enabled ? 'bg-white text-purple-700 hover:bg-purple-100' : 'bg-gray-50 text-gray-300 cursor-not-allowed'}`}
                              >
                                <Icon className="w-3.5 h-3.5" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── TRANSFORM ───────────────────────────────────────────── */}
                  <div className="flex items-center gap-2 py-2">
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Transform</span>
                    <div className="flex-1 h-px bg-gray-100" />
                  </div>
                  <div className="space-y-2 pb-1">
                    {/* Position + Size compact 4-up grid */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-gray-400 uppercase tracking-wide">X</Label>
                        <Input type="number" value={Math.round(selectedElement.x)} onChange={(e) => handlePropertyChange('x', Number(e.target.value))} className="h-8 mt-0.5 text-sm" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Y</Label>
                        <Input type="number" value={Math.round(selectedElement.y)} onChange={(e) => handlePropertyChange('y', Number(e.target.value))} className="h-8 mt-0.5 text-sm" />
                      </div>
                      <div className="col-span-2 flex items-end gap-1.5">
                        <div className="flex-1">
                          <Label className="text-[10px] text-gray-400 uppercase tracking-wide">W</Label>
                          <Input type="number" value={Math.round(selectedElement.width)} onChange={(e) => handleDimensionChange('width', Number(e.target.value))} className="h-8 mt-0.5 text-sm" />
                        </div>
                        <button
                          type="button"
                          onClick={toggleAspectLock}
                          title={isAspectLocked(selectedElement) ? 'Aspect ratio locked — click to unlink' : 'Aspect ratio unlocked — click to link'}
                          aria-pressed={isAspectLocked(selectedElement)}
                          data-testid="aspect-lock-toggle"
                          className={`h-8 w-8 flex-shrink-0 flex items-center justify-center rounded-md border transition-colors ${
                            isAspectLocked(selectedElement)
                              ? 'bg-violet-50 text-violet-600 border-violet-200'
                              : 'bg-white text-gray-400 border-gray-200 hover:text-gray-600'
                          }`}
                        >
                          {isAspectLocked(selectedElement) ? <Link2 className="w-4 h-4" /> : <Link2Off className="w-4 h-4" />}
                        </button>
                        <div className="flex-1">
                          <Label className="text-[10px] text-gray-400 uppercase tracking-wide">H</Label>
                          <Input type="number" value={Math.round(selectedElement.height)} onChange={(e) => handleDimensionChange('height', Number(e.target.value))} className="h-8 mt-0.5 text-sm" />
                        </div>
                      </div>
                    </div>
                    {/* Rotation + Opacity row */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Rotation</Label>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Input
                            type="number" min={0} max={360}
                            value={selectedElement.rotation || 0}
                            onChange={(e) => handlePropertyChange('rotation', Number(e.target.value))}
                            className="h-8 text-sm"
                          />
                          <span className="text-xs text-gray-400 flex-shrink-0">°</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Opacity</Label>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Input
                            type="number" min={0} max={100}
                            value={Math.round((selectedElement.opacity ?? 1) * 100)}
                            onChange={(e) => handlePropertyChange('opacity', Math.max(0, Math.min(100, Number(e.target.value))) / 100)}
                            className="h-8 text-sm"
                          />
                          <span className="text-xs text-gray-400 flex-shrink-0">%</span>
                        </div>
                      </div>
                    </div>

                    {/* ── Blend Mode (visual elements) ─────────────────────── */}
                    {supportsBlendMode(selectedElement.type) && (
                      <div className="mt-2">
                        <Label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">Blend Mode</Label>
                        <select
                          data-testid="blend-mode-select"
                          value={selectedElement.blendMode || 'normal'}
                          onChange={(e) => handleCommitProperty('blendMode', e.target.value)}
                          className="w-full h-8 text-sm border border-input rounded-md bg-transparent px-2"
                        >
                          {BLEND_MODES.map((m) => (
                            <option key={m.id} value={m.id}>{m.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* ── TYPOGRAPHY (text only) ───────────────────────────────── */}
                  {selectedElement.type === 'text' && (
                    <>
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Typography</span>
                        <div className="flex-1 h-px bg-gray-100" />
                      </div>
                      <div className="space-y-2 pb-1">
                        {/* Font family */}
                        <Select
                          value={selectedElement.fontFamily}
                          onValueChange={(val) => { loadGoogleFont(val); handlePropertyChange('fontFamily', val); }}
                        >
                          <SelectTrigger className="h-8 text-sm" style={{ fontFamily: `'${selectedElement.fontFamily || 'Poppins'}', sans-serif` }}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {EDITOR_FONTS.map(font => (
                              <FontSelectItem key={font} font={font} />
                            ))}
                          </SelectContent>
                        </Select>
                        {brandKit?.fonts?.length > 0 && (
                          <div className="flex flex-wrap gap-1 pt-0.5">
                            {brandKit.fonts.map((f) => (
                              <Button key={f.id} type="button" variant="outline" size="sm" className="h-6 text-xs px-2" style={{ fontFamily: f.name }} onClick={() => handlePropertyChange('fontFamily', f.name)}>{f.name}</Button>
                            ))}
                          </div>
                        )}
                        {/* Weight + Size */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Weight</Label>
                            <Select value={selectedElement.fontWeight || '400'} onValueChange={(val) => handlePropertyChange('fontWeight', val)}>
                              <SelectTrigger className="h-8 mt-0.5 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="300">Light</SelectItem>
                                <SelectItem value="400">Regular</SelectItem>
                                <SelectItem value="500">Medium</SelectItem>
                                <SelectItem value="600">SemiBold</SelectItem>
                                <SelectItem value="700">Bold</SelectItem>
                                <SelectItem value="800">ExtraBold</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Size (px)</Label>
                            <Input
                              type="number"
                              value={String(selectedElement.fontSize ?? 16).replace('px', '') || 16}
                              onChange={(e) => handlePropertyChange('fontSize', `${e.target.value}px`)}
                              className="h-8 mt-0.5 text-sm"
                            />
                          </div>
                        </div>
                        {/* Line height + Letter spacing */}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Line Height</Label>
                            <div className="flex gap-1 mt-0.5">
                              <button
                                type="button"
                                className={`h-8 text-xs rounded border px-2 flex-shrink-0 ${!selectedElement.lineHeight || selectedElement.lineHeight === 'auto' ? 'bg-gray-900 text-white border-gray-900' : 'bg-transparent text-gray-700 border-input hover:bg-accent'}`}
                                onClick={() => handlePropertyChange('lineHeight', 'auto')}
                              >
                                Auto
                              </button>
                              <Input
                                type="number"
                                value={selectedElement.lineHeight && selectedElement.lineHeight !== 'auto' ? selectedElement.lineHeight : ''}
                                onChange={(e) => handlePropertyChange('lineHeight', e.target.value ? Number(e.target.value) : 'auto')}
                                className="h-8 text-sm min-w-0"
                                placeholder="1.5"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Letter Spacing</Label>
                            <Input
                              type="text"
                              value={selectedElement.letterSpacing || '0%'}
                              onChange={(e) => handlePropertyChange('letterSpacing', e.target.value)}
                              className="h-8 mt-0.5 text-sm"
                              placeholder="0%"
                            />
                          </div>
                        </div>
                        {/* Alignment */}
                        <div>
                          <Label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">Alignment</Label>
                          <div className="flex gap-2">
                            {[
                              { align: 'left',   Icon: AlignLeft },
                              { align: 'center', Icon: AlignCenter },
                              { align: 'right',  Icon: AlignRight },
                            ].map(({ align, Icon }) => (
                              <button key={align} type="button"
                                onClick={() => handlePropertyChange('textAlign', align)}
                                className={`inline-flex items-center justify-center h-8 flex-1 rounded-md border text-sm transition-colors ${selectedElement.textAlign === align ? 'bg-gray-900 text-white border-gray-900' : 'border-input bg-transparent hover:bg-accent'}`}>
                                <Icon className="w-4 h-4" />
                              </button>
                            ))}
                            {[
                              { va: 'top',    Icon: ChevronUp },
                              { va: 'middle', Icon: AlignCenter },
                              { va: 'bottom', Icon: ChevronDown },
                            ].map(({ va, Icon }) => (
                              <button key={va} type="button"
                                onClick={() => handlePropertyChange('verticalAlign', va)}
                                className={`inline-flex items-center justify-center h-8 flex-1 rounded-md border text-sm transition-colors ${selectedElement.verticalAlign === va ? 'bg-gray-900 text-white border-gray-900' : 'border-input bg-transparent hover:bg-accent'}`}>
                                <Icon className="w-4 h-4" />
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* ── FORMATTING ──────────────────────────────────────── */}
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Formatting</span>
                        <div className="flex-1 h-px bg-gray-100" />
                      </div>
                      <div className="flex gap-2 pb-1">
                        {[
                          { label: 'Bold',      active: selectedElement.fontWeight === 'bold' || selectedElement.fontWeight === '700', Icon: Bold,      onClick: () => handlePropertyChange('fontWeight', (selectedElement.fontWeight === 'bold' || selectedElement.fontWeight === '700') ? 'normal' : 'bold') },
                          { label: 'Italic',    active: selectedElement.fontStyle === 'italic',                                        Icon: Italic,    onClick: () => handlePropertyChange('fontStyle', selectedElement.fontStyle === 'italic' ? 'normal' : 'italic') },
                          { label: 'Underline', active: selectedElement.textDecoration === 'underline',                               Icon: Underline, onClick: () => handlePropertyChange('textDecoration', selectedElement.textDecoration === 'underline' ? 'none' : 'underline') },
                        ].map(({ label, active, Icon, onClick }) => (
                          <button key={label} type="button" onClick={onClick} title={label}
                            className={`inline-flex items-center justify-center h-8 flex-1 rounded-md border transition-colors ${active ? 'bg-gray-900 text-white border-gray-900' : 'border-input bg-transparent hover:bg-accent'}`}>
                            <Icon className="w-4 h-4" />
                          </button>
                        ))}
                      </div>
                      {/* Lists */}
                      <div className="flex gap-2 pb-1">
                        <button
                          type="button"
                          title="Bullet list"
                          onClick={() => handlePropertyChange('listType', selectedElement.listType === 'bullet' ? null : 'bullet')}
                          className={`inline-flex items-center justify-center h-8 flex-1 rounded-md border transition-colors ${selectedElement.listType === 'bullet' ? 'bg-gray-900 text-white border-gray-900' : 'border-input bg-transparent hover:bg-accent'}`}
                        >
                          <List className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          title="Numbered list"
                          onClick={() => handlePropertyChange('listType', selectedElement.listType === 'numbered' ? null : 'numbered')}
                          className={`inline-flex items-center justify-center h-8 flex-1 rounded-md border transition-colors ${selectedElement.listType === 'numbered' ? 'bg-gray-900 text-white border-gray-900' : 'border-input bg-transparent hover:bg-accent'}`}
                        >
                          <ListOrdered className="w-4 h-4" />
                        </button>
                      </div>

                      {/* ── APPEARANCE (text) ───────────────────────────────── */}
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Appearance</span>
                        <div className="flex-1 h-px bg-gray-100" />
                      </div>
                      <div className="space-y-3 pb-1">
                        {/* Fill */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-medium text-gray-700">Fill</Label>
                            <Button size="icon" variant="ghost" className="h-6 w-6"
                              onClick={() => handlePropertyChange('fillEnabled', selectedElement.fillEnabled === false ? true : false)}>
                              {selectedElement.fillEnabled === false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </Button>
                          </div>
                          <div className="flex items-center gap-2 min-w-0">
                            <input type="color" value={selectedElement.color || '#000000'} onChange={(e) => handlePropertyChange('color', e.target.value)} className="w-8 h-8 rounded border cursor-pointer p-0.5 flex-shrink-0" />
                            <Input type="text" value={selectedElement.color || '#000000'} onChange={(e) => handlePropertyChange('color', e.target.value)} className="flex-1 min-w-0 h-8 text-xs font-mono" placeholder="#000000" />
                            <Input type="number" min={0} max={100} value={Math.round((selectedElement.fillOpacity ?? 1) * 100)} onChange={(e) => handlePropertyChange('fillOpacity', Math.max(0, Math.min(100, Number(e.target.value))) / 100)} className="w-16 h-8 text-xs flex-shrink-0" placeholder="100" />
                          </div>
                          {/* Fill Gradient under Fill */}
                          <div className="pl-0 pt-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Gradient</span>
                              {selectedElement.fillGradient && (
                                <button className="text-[10px] text-gray-400 hover:text-red-500" onClick={() => handlePropertyChange('fillGradient', null)}>Clear</button>
                              )}
                            </div>
                            <GradientEditor
                              value={selectedElement.fillGradient || { type: 'linear', colors: [selectedElement.color || '#6366f1', '#ec4899'], positions: [0, 100], opacities: [1, 1], angle: 90 }}
                              onChange={(g) => handlePropertyChange('fillGradient', g)}
                            />
                          </div>
                        </div>

                        {/* Stroke */}
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Stroke</Label>
                          <div className="flex items-center gap-2 min-w-0">
                            <input type="color" value={selectedElement.stroke || '#000000'} onChange={(e) => handlePropertyChange('stroke', e.target.value)} className="w-8 h-8 rounded border cursor-pointer p-0.5 flex-shrink-0" />
                            <Input type="text" value={selectedElement.stroke || '#000000'} onChange={(e) => handlePropertyChange('stroke', e.target.value)} className="flex-1 min-w-0 h-8 text-xs font-mono" placeholder="#000000" />
                            <Input type="number" min={0} value={selectedElement.strokeWidth || 0} onChange={(e) => handlePropertyChange('strokeWidth', Number(e.target.value || 0))} className="w-16 h-8 text-xs flex-shrink-0" placeholder="0" />
                          </div>
                          <div className="flex items-center gap-2">
                            <Select value={selectedElement.strokePosition || 'outside'} onValueChange={(val) => handlePropertyChange('strokePosition', val)}>
                              <SelectTrigger className="h-7 flex-1 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="outside">Outside</SelectItem>
                                <SelectItem value="center">Center</SelectItem>
                                <SelectItem value="inside">Inside</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input type="number" min={0} max={100} value={Math.round((selectedElement.strokeOpacity ?? 1) * 100)} onChange={(e) => handlePropertyChange('strokeOpacity', Math.max(0, Math.min(100, Number(e.target.value))) / 100)} className="w-16 h-7 text-xs" />
                          </div>
                          {/* Stroke Gradient under Stroke */}
                          <div className="pl-0 pt-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Gradient</span>
                              {selectedElement.strokeGradient && (
                                <button className="text-[10px] text-gray-400 hover:text-red-500" onClick={() => handlePropertyChange('strokeGradient', null)}>Clear</button>
                              )}
                            </div>
                            <GradientEditor
                              value={selectedElement.strokeGradient || { type: 'linear', colors: [selectedElement.stroke || '#000000', '#6366f1'], positions: [0, 100], opacities: [1, 1], angle: 90 }}
                              onChange={(g) => handlePropertyChange('strokeGradient', g)}
                            />
                          </div>
                        </div>
                      </div>

                      {/* ── EFFECTS (text) ──────────────────────────────────── */}
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Effects</span>
                        <div className="flex-1 h-px bg-gray-100" />
                      </div>
                      <div className="space-y-3 pb-1">
                        {/* Text Shadow */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-medium text-gray-700">Text Shadow</Label>
                            <button className="text-[10px] text-gray-400 hover:text-red-500" onClick={() => onUpdateElement(selectedElement.id, { shadowColor: undefined, shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0, shadowOpacity: 1 })}>Clear</button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px] text-gray-400">Color</Label>
                              <Input type="color" value={selectedElement.shadowColor ?? '#000000'} onChange={(e) => handlePropertyChange('shadowColor', e.target.value)} className="h-8 mt-0.5 w-full" />
                            </div>
                            <div>
                              <Label className="text-[10px] text-gray-400">Blur</Label>
                              <Input type="number" min={0} max={50} value={selectedElement.shadowBlur ?? 0} onChange={(e) => handlePropertyChange('shadowBlur', Math.max(0, Math.min(50, Number(e.target.value) || 0)))} className="h-8 mt-0.5 text-sm" />
                            </div>
                            <div>
                              <Label className="text-[10px] text-gray-400">Offset X</Label>
                              <Input type="number" value={selectedElement.shadowOffsetX ?? 0} onChange={(e) => handlePropertyChange('shadowOffsetX', Number(e.target.value) || 0)} className="h-8 mt-0.5 text-sm" />
                            </div>
                            <div>
                              <Label className="text-[10px] text-gray-400">Offset Y</Label>
                              <Input type="number" value={selectedElement.shadowOffsetY ?? 0} onChange={(e) => handlePropertyChange('shadowOffsetY', Number(e.target.value) || 0)} className="h-8 mt-0.5 text-sm" />
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between">
                              <Label className="text-[10px] text-gray-400">Opacity</Label>
                              <span className="text-[10px] text-gray-400">{Math.round((Number.isFinite(Number(selectedElement.shadowOpacity)) ? Number(selectedElement.shadowOpacity) : 1) * 100)}%</span>
                            </div>
                            <Slider
                              value={[Math.max(0, Math.min(1, Number.isFinite(Number(selectedElement.shadowOpacity)) ? Number(selectedElement.shadowOpacity) : 1))]}
                              onValueChange={([v]) => handlePropertyChange('shadowOpacity', v)}
                              min={0} max={1} step={0.05} className="mt-1"
                            />
                          </div>
                        </div>

                        {/* Curved Text */}
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Curved Text</Label>
                          <div className="flex items-center justify-between text-xs mb-0.5">
                            <span className="text-gray-400">Curvature</span>
                            <span className="tabular-nums text-gray-600">{Number.isFinite(Number(selectedElement.textCurve)) ? Number(selectedElement.textCurve) : 0}°</span>
                          </div>
                          <Slider
                            value={[(() => { const n = Number(selectedElement.textCurve); return Number.isFinite(n) ? Math.max(-360, Math.min(360, n)) : 0; })()]}
                            onValueChange={([v]) => handlePropertyChange('textCurve', v)}
                            min={-360} max={360} step={1}
                            aria-label="Text curvature"
                          />
                          <div className="flex items-center justify-between text-[10px] text-gray-400">
                            <span>− Curve down</span><span>+ Curve up</span>
                          </div>
                          <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={() => handlePropertyChange('textCurve', 0)}>
                            Reset to straight
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── APPEARANCE (shape) ──────────────────────────────────── */}
                  {selectedElement.type === 'shape' && (
                    <>
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Appearance</span>
                        <div className="flex-1 h-px bg-gray-100" />
                      </div>
                      <div className="space-y-3 pb-1">
                        {/* Fill */}
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Fill</Label>
                          <div className="flex items-center gap-2 min-w-0">
                            <input type="color" value={selectedElement.fill || '#6366f1'} onChange={(e) => handlePropertyChange('fill', e.target.value)} className="w-8 h-8 rounded border cursor-pointer p-0.5 flex-shrink-0" />
                            <Input type="text" value={selectedElement.fill || '#6366f1'} onChange={(e) => handlePropertyChange('fill', e.target.value)} className="flex-1 min-w-0 h-8 text-xs font-mono" />
                          </div>
                          {brandKit?.colors?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {brandKit.colors.map((c) => (
                                <button key={c.id} type="button" className="w-6 h-6 rounded border-2 border-transparent hover:border-purple-400 transition-colors" style={{ background: c.hex }} title={c.name} onClick={() => handlePropertyChange('fill', c.hex)} />
                              ))}
                            </div>
                          )}
                          {/* Fill Gradient */}
                          <div className="pt-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Gradient</span>
                              {selectedElement.fillGradient && <button className="text-[10px] text-gray-400 hover:text-red-500" onClick={() => handlePropertyChange('fillGradient', null)}>Clear</button>}
                            </div>
                            <GradientEditor
                              value={selectedElement.fillGradient || { type: 'linear', colors: [selectedElement.fill || '#6366f1', '#ec4899'], positions: [0, 100], opacities: [1, 1], angle: 90 }}
                              onChange={(g) => handlePropertyChange('fillGradient', g)}
                            />
                          </div>
                        </div>
                        {/* Stroke */}
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Stroke</Label>
                          <div className="flex items-center gap-2 min-w-0">
                            <input type="color" value={selectedElement.stroke || '#000000'} onChange={(e) => handlePropertyChange('stroke', e.target.value)} className="w-8 h-8 rounded border cursor-pointer p-0.5 flex-shrink-0" />
                            <Input type="text" value={selectedElement.stroke || '#000000'} onChange={(e) => handlePropertyChange('stroke', e.target.value)} className="flex-1 min-w-0 h-8 text-xs font-mono" />
                            <Input type="number" min={0} max={50} value={Math.max(0, Math.min(50, Number(selectedElement.strokeWidth) || 0))} onChange={(e) => handlePropertyChange('strokeWidth', Math.max(0, Math.min(50, Number(e.target.value) || 0)))} className="w-16 h-8 text-xs flex-shrink-0" placeholder="0" />
                          </div>
                          {/* Stroke Gradient */}
                          <div className="pt-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Gradient</span>
                              {selectedElement.strokeGradient && <button className="text-[10px] text-gray-400 hover:text-red-500" onClick={() => handlePropertyChange('strokeGradient', null)}>Clear</button>}
                            </div>
                            <GradientEditor
                              value={selectedElement.strokeGradient || { type: 'linear', colors: [selectedElement.stroke || '#000000', '#6366f1'], positions: [0, 100], opacities: [1, 1], angle: 90 }}
                              onChange={(g) => handlePropertyChange('strokeGradient', g)}
                            />
                          </div>
                        </div>
                        {/* Corner Radius */}
                        <CornerRadiusEditor
                          element={selectedElement}
                          onChange={handlePropertyChange}
                        />
                      </div>
                    </>
                  )}

                  {/* ── APPEARANCE (svg / Memphis / uploaded SVG) ────────────── */}
                  {selectedElement.type === 'svg' && (
                    <>
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Colors</span>
                        <div className="flex-1 h-px bg-gray-100" />
                      </div>
                      <div className="space-y-3 pb-1">
                        {/* Uploaded SVG: Smart Palette (Primary/Secondary/Accent)
                            with collapsible Advanced section containing the full
                            per-color and per-gradient editors. */}
                        {selectedElement.colorMap && Object.keys(selectedElement.colorMap).length > 0 ? (
                          <SvgPaletteEditor
                            selectedElement={selectedElement}
                            onUpdateElement={onUpdateElement}
                            brandKit={brandKit}
                          />
                        ) : selectedElement.colorMap ? (
                          /* Uploaded SVG with no detected hex/rgb colors — show tint picker */
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-gray-700">Tint Color</Label>
                            <p className="text-[10px] text-gray-400 leading-relaxed">
                              No editable colors detected (SVG may use CSS classes or named colors). A tint will be applied.
                            </p>
                            <div className="flex items-center gap-2 min-w-0">
                              <input
                                type="color"
                                value={selectedElement.fill || '#000000'}
                                onChange={(e) => handlePropertyChange('fill', e.target.value)}
                                className="w-8 h-8 rounded border cursor-pointer p-0.5 flex-shrink-0"
                              />
                              <Input
                                type="text"
                                value={selectedElement.fill || '#000000'}
                                onChange={(e) => handlePropertyChange('fill', e.target.value)}
                                className="flex-1 min-w-0 h-8 text-xs font-mono"
                              />
                            </div>
                            {brandKit?.colors?.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {brandKit.colors.map((c) => (
                                  <button
                                    key={c.id}
                                    type="button"
                                    className="w-6 h-6 rounded border-2 border-transparent hover:border-purple-400 transition-colors"
                                    style={{ background: c.hex }}
                                    title={c.name}
                                    onClick={() => handlePropertyChange('fill', c.hex)}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          /* Memphis SVG: single fill color */
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-gray-700">Fill Color</Label>
                            <div className="flex items-center gap-2 min-w-0">
                              <input
                                type="color"
                                value={selectedElement.fill || '#6366f1'}
                                onChange={(e) => handlePropertyChange('fill', e.target.value)}
                                className="w-8 h-8 rounded border cursor-pointer p-0.5 flex-shrink-0"
                              />
                              <Input
                                type="text"
                                value={selectedElement.fill || '#6366f1'}
                                onChange={(e) => handlePropertyChange('fill', e.target.value)}
                                className="flex-1 min-w-0 h-8 text-xs font-mono"
                              />
                            </div>
                            {brandKit?.colors?.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {brandKit.colors.map((c) => (
                                  <button
                                    key={c.id}
                                    type="button"
                                    className="w-6 h-6 rounded border-2 border-transparent hover:border-purple-400 transition-colors"
                                    style={{ background: c.hex }}
                                    title={c.name}
                                    onClick={() => handlePropertyChange('fill', c.hex)}
                                  />
                                ))}
                              </div>
                            )}
                            <p className="text-[10px] text-gray-400">Memphis shape · scales without quality loss</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* ── STROKE (svg) ─────────────────────────────────────────── */}
                  {selectedElement.type === 'svg' && (() => {
                    const stroke = { ...DEFAULT_STROKE, ...selectedElement.stroke };
                    const updateStroke = (changes) =>
                      handlePropertyChange('stroke', { ...stroke, ...changes });
                    return (
                      <>
                        <div className="flex items-center justify-between py-2 gap-2">
                          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest flex-shrink-0">Stroke</span>
                          <div className="flex-1 h-px bg-gray-100 min-w-0" />
                          <ToggleSwitch enabled={stroke.enabled} onToggle={() => updateStroke({ enabled: !stroke.enabled })} />
                        </div>
                        {stroke.enabled && (
                          <div className="space-y-3 pb-1">
                            <div className="flex items-center gap-2 min-w-0">
                              <input
                                type="color"
                                value={stroke.color}
                                onChange={(e) => updateStroke({ color: e.target.value })}
                                className="w-8 h-8 rounded border cursor-pointer p-0.5 flex-shrink-0"
                              />
                              <Input
                                type="text"
                                value={stroke.color}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (/^#[0-9a-fA-F]{6}$/.test(v)) updateStroke({ color: v });
                                }}
                                className="flex-1 min-w-0 h-8 text-xs font-mono"
                                maxLength={7}
                              />
                            </div>
                            {/* Width */}
                            <div className="space-y-1">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs font-medium text-gray-700">Width</Label>
                                <span className="text-xs text-gray-500">{stroke.width}px</span>
                              </div>
                              <input
                                type="range"
                                min={0}
                                max={20}
                                step={0.5}
                                value={stroke.width}
                                onChange={(e) => updateStroke({ width: parseFloat(e.target.value) })}
                                className="w-full accent-purple-500"
                              />
                            </div>
                            {/* Position */}
                            <div className="space-y-1">
                              <Label className="text-xs font-medium text-gray-700">Position</Label>
                              <div className="flex gap-1">
                                {(['center', 'outside', 'inside']).map((pos) => (
                                  <button
                                    key={pos}
                                    type="button"
                                    onClick={() => updateStroke({ position: pos })}
                                    className={`flex-1 py-1 text-[10px] rounded border transition-colors capitalize ${
                                      stroke.position === pos
                                        ? 'bg-purple-600 text-white border-purple-600'
                                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-purple-300'
                                    }`}
                                  >
                                    {pos}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* ── FILL GRADIENT (svg) ───────────────────────────────────── */}
                  {selectedElement.type === 'svg' && (() => {
                    const grad = { ...DEFAULT_GRADIENT, ...selectedElement.gradient };
                    const updateGrad = (changes) =>
                      handlePropertyChange('gradient', { ...grad, ...changes });
                    const colorEntries = Object.entries(selectedElement.colorMap || {});
                    const isPerColor = grad.mode === 'per-color';

                    return (
                      <>
                        <div className="flex items-center justify-between py-2 gap-2">
                          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest flex-shrink-0">Fill Gradient</span>
                          <div className="flex-1 h-px bg-gray-100 min-w-0" />
                          <ToggleSwitch enabled={grad.enabled} onToggle={() => updateGrad({ enabled: !grad.enabled })} />
                        </div>

                        {grad.enabled && (
                          <div className="space-y-3 pb-1">
                            {/* Mode toggle — only show when SVG has multiple colors */}
                            {colorEntries.length > 0 && (
                              <div className="flex gap-1">
                                {[['global', 'Entire SVG'], ['per-color', 'Per Color']].map(([m, label]) => (
                                  <button
                                    key={m}
                                    type="button"
                                    onClick={() => updateGrad({ mode: m })}
                                    className={`flex-1 py-1 text-[10px] rounded border transition-colors ${
                                      grad.mode === m
                                        ? 'bg-purple-600 text-white border-purple-600'
                                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-purple-300'
                                    }`}
                                  >
                                    {label}
                                  </button>
                                ))}
                              </div>
                            )}

                            {/* ── Global mode: full GradientEditor ── */}
                            {!isPerColor && (
                              <>
                                <div
                                  className="h-6 rounded border border-gray-200"
                                  style={{ background: buildGradientCSS(grad) }}
                                />
                                <GradientEditor
                                  value={grad}
                                  onChange={(newGrad) => updateGrad(newGrad)}
                                />
                              </>
                            )}

                            {/* ── Per-color mode ── */}
                            {isPerColor && colorEntries.map(([original, current], idx) => {
                              const perColorGrad = {
                                type:      'linear',
                                colors:    [current, '#ffffff'],
                                positions: [0, 100],
                                opacities: [1, 1],
                                angle:     90,
                                ...(grad.perColor?.[original] || {}),
                              };
                              const isGradEnabled = !!grad.perColor?.[original]?.enabled;

                              const updatePerColor = (patch) =>
                                updateGrad({
                                  perColor: {
                                    ...grad.perColor,
                                    [original]: { ...perColorGrad, ...patch },
                                  },
                                });

                              return (
                                <div key={original} className="rounded-xl border border-gray-100 p-2.5 space-y-2">
                                  {/* Header row */}
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-4 h-4 rounded-full border border-gray-200 flex-shrink-0"
                                      style={{ background: current }}
                                    />
                                    <span className="text-[10px] text-gray-500 font-medium flex-1 truncate font-mono">
                                      Color {idx + 1}
                                    </span>
                                    {/* Solid / Gradient toggle */}
                                    <div className="flex gap-0.5">
                                      {[['solid', 'Solid'], ['gradient', 'Gradient']].map(([mode, label]) => (
                                        <button
                                          key={mode}
                                          type="button"
                                          onClick={() => updatePerColor({ enabled: mode === 'gradient' })}
                                          className={`px-2 py-0.5 text-[9px] rounded border transition-colors ${
                                            (mode === 'gradient') === isGradEnabled
                                              ? 'bg-purple-600 text-white border-purple-600'
                                              : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-purple-300'
                                          }`}
                                        >
                                          {label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Gradient controls for this color */}
                                  {isGradEnabled && (
                                    <div className="pt-1 space-y-2">
                                      <div
                                        className="h-5 rounded border border-gray-200"
                                        style={{ background: buildGradientCSS(perColorGrad) }}
                                      />
                                      <GradientEditor
                                        value={perColorGrad}
                                        onChange={(newGrad) => updatePerColor(newGrad)}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}

                            <button
                              type="button"
                              onClick={() => handlePropertyChange('gradient', { ...DEFAULT_GRADIENT, enabled: false })}
                              className="text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              Remove gradient
                            </button>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {/* ── APPEARANCE (penPath) ─────────────────────────────────── */}
                  {selectedElement.type === 'penPath' && (
                    <>
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Appearance</span>
                        <div className="flex-1 h-px bg-gray-100" />
                      </div>
                      <div className="space-y-3 pb-1">
                        {/* Fill */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-medium text-gray-700">Fill</Label>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] text-gray-400">Enable</span>
                              <ToggleSwitch
                                size="sm"
                                enabled={!!(selectedElement.fill && selectedElement.fill !== 'none')}
                                onToggle={() => handlePropertyChange('fill', selectedElement.fill && selectedElement.fill !== 'none' ? 'none' : '#6366f1')}
                              />
                            </div>
                          </div>
                          {selectedElement.fill && selectedElement.fill !== 'none' && (
                            <>
                              <div className="flex items-center gap-2 min-w-0">
                                <input type="color" value={selectedElement.fill === 'none' ? '#6366f1' : (selectedElement.fill || '#6366f1')} onChange={(e) => handlePropertyChange('fill', e.target.value)} className="w-8 h-8 rounded border cursor-pointer p-0.5 flex-shrink-0" />
                                <Input type="text" value={selectedElement.fill === 'none' ? '#6366f1' : (selectedElement.fill || '#6366f1')} onChange={(e) => handlePropertyChange('fill', e.target.value)} className="flex-1 min-w-0 h-8 text-xs font-mono" />
                              </div>
                              {/* Fill Gradient */}
                              <div className="pt-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Gradient</span>
                                  {selectedElement.fillGradient && <button className="text-[10px] text-gray-400 hover:text-red-500" onClick={() => handlePropertyChange('fillGradient', null)}>Clear</button>}
                                </div>
                                <GradientEditor
                                  value={selectedElement.fillGradient || { type: 'linear', colors: [selectedElement.fill || '#6366f1', '#ec4899'], positions: [0, 100], opacities: [1, 1], angle: 90 }}
                                  onChange={(g) => handlePropertyChange('fillGradient', g)}
                                />
                              </div>
                            </>
                          )}
                        </div>
                        {/* Stroke */}
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Stroke</Label>
                          <div className="flex items-center gap-2 min-w-0">
                            <input type="color" value={selectedElement.stroke || '#000000'} onChange={(e) => handlePropertyChange('stroke', e.target.value)} className="w-8 h-8 rounded border cursor-pointer p-0.5 flex-shrink-0" />
                            <Input type="text" value={selectedElement.stroke || '#000000'} onChange={(e) => handlePropertyChange('stroke', e.target.value)} className="flex-1 min-w-0 h-8 text-xs font-mono" />
                          </div>
                          <div className="flex items-center gap-2 min-w-0">
                            <Label className="text-[10px] text-gray-400 w-10 flex-shrink-0">Width</Label>
                            <Slider
                              value={[Math.max(0, Math.min(500, Number(selectedElement.strokeWidth) ?? 2))]}
                              onValueChange={(val) => handlePropertyChange('strokeWidth', Math.max(0, Math.min(500, Number(val[0]))))}
                              min={0} max={500} step={1} className="flex-1 min-w-0"
                            />
                            <Input type="number" value={Math.max(0, Math.min(500, Number(selectedElement.strokeWidth) ?? 2))} onChange={(e) => handlePropertyChange('strokeWidth', Math.max(0, Math.min(500, Number(e.target.value) || 0)))} min={0} max={500} className="w-16 h-7 text-xs flex-shrink-0" />
                          </div>
                          {/* Stroke Gradient */}
                          <div className="pt-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">Gradient</span>
                              {selectedElement.strokeGradient && <button className="text-[10px] text-gray-400 hover:text-red-500" onClick={() => handlePropertyChange('strokeGradient', null)}>Clear</button>}
                            </div>
                            <GradientEditor
                              value={selectedElement.strokeGradient || { type: 'linear', colors: [selectedElement.stroke || '#000000', '#6366f1'], positions: [0, 100], opacities: [1, 1], angle: 90 }}
                              onChange={(g) => handlePropertyChange('strokeGradient', g)}
                            />
                          </div>
                        </div>
                        <p className="text-[10px] text-gray-400">
                          {selectedElement.closed ? 'Closed shape' : 'Open path'} · {selectedElement.points?.length || 0} anchor points
                        </p>
                      </div>
                    </>
                  )}

                  {/* ── Brush Stroke ─────────────────────────────────────────── */}
                  {selectedElement.type === 'brush' && (() => {
                    const currentWidth = Math.max(1, Math.min(500, Number(selectedElement.strokeWidth) || 4));
                    return (
                      <>
                        <div className="flex items-center gap-2 py-2">
                          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Appearance</span>
                          <div className="flex-1 h-px bg-gray-100" />
                        </div>
                        <div className="space-y-2 pb-1">
                          <div>
                            <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Brush Type</Label>
                            <Select value={selectedElement.brushType || 'basic'} onValueChange={val => handlePropertyChange('brushType', val)}>
                              <SelectTrigger className="h-8 mt-0.5 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {BRUSH_PRESET_OPTIONS.map(p => (
                                  <SelectItem key={p.id} value={p.id}>
                                    <span className="font-medium">{p.name}</span>
                                    <span className="ml-1.5 text-gray-400 text-xs">— {p.description}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Color</Label>
                            <div className="flex gap-2 mt-0.5">
                              <input type="color" value={selectedElement.stroke || '#000000'} onChange={(e) => handlePropertyChange('stroke', e.target.value)} className="w-8 h-8 rounded border cursor-pointer p-0.5 flex-shrink-0" />
                              <Input type="text" value={selectedElement.stroke || '#000000'} onChange={(e) => handlePropertyChange('stroke', e.target.value)} className="flex-1 h-8 text-xs font-mono" placeholder="#000000" />
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between">
                              <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Width</Label>
                              <span className="text-[10px] text-gray-400 tabular-nums">{currentWidth}px</span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <Slider value={[currentWidth]} onValueChange={(val) => handlePropertyChange('strokeWidth', Math.max(1, Math.min(500, Number(val[0]) || 1)))} min={1} max={500} step={1} className="flex-1" />
                              <Input type="number" value={currentWidth} onChange={(e) => handlePropertyChange('strokeWidth', Math.max(1, Math.min(500, Number(e.target.value) || 1)))} className="w-14 h-7 text-xs" min={1} max={500} />
                            </div>
                          </div>
                          <p className="text-[10px] text-gray-400 pt-1">Resize to scale the stroke bounding box.</p>
                        </div>
                      </>
                    );
                  })()}

                  {/* ── Drawing ──────────────────────────────────────────────── */}
                  {selectedElement.type === 'drawing' && (
                    <>
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Appearance</span>
                        <div className="flex-1 h-px bg-gray-100" />
                      </div>
                      <div className="space-y-2 pb-1">
                        <div>
                          <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Color</Label>
                          <div className="flex gap-2 mt-0.5">
                            <input type="color" value={selectedElement.stroke || '#000000'} onChange={(e) => handlePropertyChange('stroke', e.target.value)} className="w-8 h-8 rounded border cursor-pointer p-0.5 flex-shrink-0" />
                            <Input type="text" value={selectedElement.stroke || '#000000'} onChange={(e) => handlePropertyChange('stroke', e.target.value)} className="flex-1 h-8 text-xs font-mono" />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between">
                            <Label className="text-[10px] text-gray-400 uppercase tracking-wide">Width</Label>
                            <span className="text-[10px] text-gray-400 tabular-nums">{Math.max(1, Number(selectedElement.strokeWidth) || 2)}px</span>
                          </div>
                          <Slider
                            value={[Math.max(1, Math.min(100, Number(selectedElement.strokeWidth) || 2))]}
                            onValueChange={(val) => handlePropertyChange('strokeWidth', Math.max(1, Math.min(100, Number(val[0]) || 2)))}
                            min={1} max={100} step={1} className="mt-1"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── Chart ──────────────────────────────────────────────── */}
                  {selectedElement.type === 'chart' && (
                    <div className="flex items-center gap-2 py-2">
                      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Chart</span>
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>
                  )}
                  {selectedElement.type === 'chart' && (() => {
                    const DC = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f59e0b', '#10b981'];
                    // Normalize data: support legacy number[] and new {label,value}[]
                    const rawData = selectedElement.data || [];
                    const data = rawData.map((d, i) =>
                      typeof d === 'number'
                        ? { label: `Item ${i + 1}`, value: Number(d) }
                        : { label: d.label || `Item ${i + 1}`, value: Number(d.value) || 0 }
                    );
                    const dataColors = selectedElement.dataColors || DC.slice(0, data.length);
                    const isPie = selectedElement.chartType === 'pie' || selectedElement.chartType === 'donut';

                    const setData = (next) => handlePropertyChange('data', next);
                    const setDataColors = (next) => handlePropertyChange('dataColors', next);

                    const updateItem = (i, patch) => {
                      const next = data.map((d, j) => j === i ? { ...d, ...patch } : d);
                      setData(next);
                    };
                    const updateColor = (i, color) => {
                      const next = [...dataColors];
                      while (next.length <= i) next.push(DC[next.length % DC.length]);
                      next[i] = color;
                      setDataColors(next);
                    };
                    const addRow = () => {
                      if (data.length >= 10) return;
                      setData([...data, { label: `Item ${data.length + 1}`, value: 50 }]);
                      setDataColors([...dataColors, DC[data.length % DC.length]]);
                    };
                    const removeRow = (i) => {
                      if (data.length <= 2) return;
                      setData(data.filter((_, j) => j !== i));
                      setDataColors(dataColors.filter((_, j) => j !== i));
                    };

                    return (
                      <>
                        {/* Chart Type */}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold">Chart Type</Label>
                          <Select value={selectedElement.chartType} onValueChange={(val) => handlePropertyChange('chartType', val)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="bar">Bar Chart</SelectItem>
                              <SelectItem value="column">Column Chart</SelectItem>
                              <SelectItem value="line">Line Chart</SelectItem>
                              <SelectItem value="area">Area Chart</SelectItem>
                              <SelectItem value="pie">Pie Chart</SelectItem>
                              <SelectItem value="donut">Donut Chart</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Data */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold">Data</Label>
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                              onClick={addRow} disabled={data.length >= 10}>+ Add</Button>
                          </div>
                          <div className="space-y-1.5 max-h-56 overflow-auto pr-0.5">
                            {data.map((row, i) => (
                              <div key={i} className="flex gap-1.5 items-center">
                                <input
                                  type="color"
                                  value={dataColors[i] || DC[i % DC.length]}
                                  onChange={(e) => updateColor(i, e.target.value)}
                                  className="w-7 h-7 rounded border cursor-pointer flex-shrink-0 p-0.5"
                                  title="Bar color"
                                />
                                <Input
                                  placeholder="Label"
                                  value={row.label}
                                  onChange={(e) => updateItem(i, { label: e.target.value })}
                                  className="h-7 text-xs flex-1 min-w-0"
                                />
                                <Input
                                  type="number"
                                  value={row.value}
                                  onChange={(e) => updateItem(i, { value: Math.max(0, Number(e.target.value) || 0) })}
                                  className="h-7 text-xs w-16 flex-shrink-0"
                                  min="0"
                                />
                                {data.length > 2 && (
                                  <button onClick={() => removeRow(i)} className="text-gray-400 hover:text-red-500 flex-shrink-0">
                                    <span className="text-sm leading-none">×</span>
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Axis Titles */}
                        {!isPie && (
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold">Axis Titles</Label>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs text-gray-500">X-axis</Label>
                                <Input
                                  placeholder="e.g. Months"
                                  value={selectedElement.xAxisTitle || ''}
                                  onChange={(e) => handlePropertyChange('xAxisTitle', e.target.value)}
                                  className="h-7 text-xs"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-gray-500">Y-axis</Label>
                                <Input
                                  placeholder="e.g. Sales"
                                  value={selectedElement.yAxisTitle || ''}
                                  onChange={(e) => handlePropertyChange('yAxisTitle', e.target.value)}
                                  className="h-7 text-xs"
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Display Options */}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold">Display Options</Label>
                          <div className="space-y-2">
                            {[
                              { label: 'Grid Lines',   key: 'showGridLines',   hide: isPie },
                              { label: 'Data Labels',  key: 'showDataLabels' },
                              { label: 'Legend',       key: 'showLegend' },
                            ].filter(o => !o.hide).map(({ label, key }) => {
                              const val = selectedElement[key] !== false;
                              return (
                                <div key={key} className="flex items-center justify-between">
                                  <span className="text-xs text-gray-600">{label}</span>
                                  <ToggleSwitch size="sm" enabled={val} onToggle={() => handlePropertyChange(key, !val)} />
                                </div>
                              );
                            })}
                            {selectedElement.showLegend !== false && (
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-600">Legend Position</span>
                                <Select
                                  value={selectedElement.legendPosition || 'bottom'}
                                  onValueChange={(v) => handlePropertyChange('legendPosition', v)}
                                >
                                  <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="bottom">Bottom</SelectItem>
                                    <SelectItem value="top-right">Top Right</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Line/Area Color */}
                        {!isPie && (
                          <div className="space-y-2">
                            <Label className="text-sm font-semibold">
                              {(selectedElement.chartType === 'line' || selectedElement.chartType === 'area') ? 'Line Color' : 'Accent Color'}
                            </Label>
                            <div className="flex gap-2 items-center">
                              <Input type="color" value={selectedElement.color || '#6366f1'}
                                onChange={(e) => handlePropertyChange('color', e.target.value)}
                                className="w-10 h-8 p-1" />
                              <Input value={selectedElement.color || '#6366f1'}
                                onChange={(e) => handlePropertyChange('color', e.target.value)}
                                className="flex-1 h-8 text-xs" />
                            </div>
                          </div>
                        )}

                        {/* Background */}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold">Background</Label>
                          <div className="flex gap-2">
                            <Input type="color"
                              value={selectedElement.chartBackground === 'transparent' ? '#ffffff' : (selectedElement.chartBackground || '#ffffff')}
                              onChange={(e) => handlePropertyChange('chartBackground', e.target.value)}
                              className="w-10 h-8 p-1" />
                            <Button
                              variant={selectedElement.chartBackground === 'transparent' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handlePropertyChange('chartBackground', selectedElement.chartBackground === 'transparent' ? '#ffffff' : 'transparent')}
                              className="flex-1 h-8 text-xs"
                            >
                              Transparent
                            </Button>
                          </div>
                        </div>
                      </>
                    );
                  })()}

                  {/* ── Table ──────────────────────────────────────────────── */}
                  {selectedElement.type === 'table' && (
                    <div className="flex items-center gap-2 py-2">
                      <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Table</span>
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>
                  )}
                  {selectedElement.type === 'table' && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2 min-w-0">
                          <Label className="text-sm font-semibold">Rows</Label>
                          <Input
                            type="number"
                            value={selectedElement.rows || 3}
                            onChange={(e) => handlePropertyChange('rows', Number(e.target.value))}
                            min="2"
                            max="20"
                          />
                        </div>
                        <div className="space-y-2 min-w-0">
                          <Label className="text-sm font-semibold">Columns</Label>
                          <Input
                            type="number"
                            value={selectedElement.cols || 3}
                            onChange={(e) => handlePropertyChange('cols', Number(e.target.value))}
                            min="2"
                            max="10"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Header Color</Label>
                        <Input
                          type="color"
                          value={selectedElement.headerColor || '#6366f1'}
                          onChange={(e) => handlePropertyChange('headerColor', e.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm font-semibold">Border Color</Label>
                        <Input
                          type="color"
                          value={selectedElement.borderColor || '#d1d5db'}
                          onChange={(e) => handlePropertyChange('borderColor', e.target.value)}
                        />
                      </div>
                    </>
                  )}

                  {/* ── Replace Image ──────────────────────────────────────── */}
                  {selectedElement.type === 'image' && onReplaceImage && (
                    <div className="pt-2 pb-1">
                      <input
                        ref={replaceImageInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            onReplaceImage(selectedElement.id, ev.target.result);
                          };
                          reader.readAsDataURL(file);
                          e.target.value = '';
                        }}
                      />
                      <Button
                        variant="outline"
                        className="w-full text-violet-700 border-violet-200 hover:bg-violet-50 gap-2"
                        onClick={() => replaceImageInputRef.current?.click()}
                      >
                        <ImageIcon className="w-4 h-4" />
                        Replace Image
                      </Button>
                    </div>
                  )}

                  {/* ── EFFECTS gallery (Canva-style visual browser) ──────────
                      Three independently collapsible categories — Effect /
                      Filter / Shadow — of live preview cards. Reuses the
                      propertyEffects engine and the element.effect/filterPreset/
                      shadow fields (same as the compact controls above). */}
                  {(supportsBlendMode(selectedElement.type) || selectedElement.type === 'text') && (
                    <>
                      <div className="flex items-center gap-2 py-2">
                        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Effects</span>
                        <div className="flex-1 h-px bg-gray-100" />
                      </div>
                      <EffectGallery
                        element={selectedElement}
                        onCommit={handleCommitProperty}
                        onLive={handlePropertyChange}
                      />
                    </>
                  )}

                </div>
              )}
            </ScrollArea>

            {/* Actions (only shown when an element is selected) */}
            {(selectedElement || (selectedElements && selectedElements.length > 0)) && (
              <div className="p-4 border-t flex-shrink-0 bg-white">
                <div className="space-y-2">
                  <Button
                    onClick={() => (selectedElements?.length > 1 && onDuplicateSelectedElements) ? onDuplicateSelectedElements() : onDuplicateElement(selectedElement)}
                    variant="outline"
                    className="w-full"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    {selectedElements?.length > 1 ? `Duplicate ${selectedElements.length} layers` : 'Duplicate'}
                  </Button>
                  <Button
                    onClick={() => (selectedElements?.length > 1 && onDeleteSelectedElements) ? onDeleteSelectedElements() : onDeleteElement(selectedElement.id)}
                    variant="destructive"
                    className="w-full"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {selectedElements?.length > 1 ? `Delete ${selectedElements.length} layers` : 'Delete'}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        )}

        {/* Layers Tab — hierarchical tree (groups + nested children) via
            LayersPanel. Cascade visibility/lock + expand/collapse + multi-
            select all live in the panel; selection sync is two-way through
            onSelectElement / selectedElements. */}
        {activeTab === 'layers' && (
          <TabsContent value="layers" className="flex-1 overflow-auto m-0">
            <div className="p-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600">{elements?.length || 0} {elements?.length === 1 ? 'layer' : 'layers'}</p>
              </div>
              {elements && elements.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No layers yet
                </div>
              ) : (
                <LayersPanel
                  elements={elements}
                  selectedElement={selectedElement}
                  selectedElements={selectedElements}
                  onSelectElement={onSelectElement}
                  onUpdateElement={onUpdateElement}
                  onMoveLayer={onMoveLayer}
                  onReorderLayer={onReorderLayer}
                />
              )}
            </div>
          </TabsContent>
        )}


      </Tabs>
    </div>
  );
};

export default RightSidebar;
